import { useDropzone } from "react-dropzone"
import cx from "classnames"

interface FileMetadata {
  name: string
  size: number
  type: string
  lastModified: Date
  extension: string
  isValid: boolean
}

interface SpatialFileUploadProps {
  onFilesSelected: (files: File[], metadata: FileMetadata[]) => void
  onFilesRemoved: () => void
  onError?: (error: string) => void
  error?: string | null
  isProcessing?: boolean
  processingStatus?: string
}

const SUPPORTED_EXTENSIONS = [".kml", ".zip"]
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes

const getFileMetadata = (file: File): FileMetadata => {
  const extension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf("."))
  const isValid =
    SUPPORTED_EXTENSIONS.includes(extension) && file.size <= MAX_FILE_SIZE

  return {
    name: file.name,
    size: file.size,
    type: file.type || "Unknown",
    lastModified: new Date(file.lastModified),
    extension,
    isValid,
  }
}

export const SpatialFileUpload = ({
  onFilesSelected,
  onFilesRemoved: _onFilesRemoved,
  onError,
  error,
  isProcessing = false,
  processingStatus = "Processing file...",
}: SpatialFileUploadProps) => {
  const handleDrop = (files: File[]) => {
    const metadata = files.map(getFileMetadata)
    onFilesSelected(files, metadata)
  }

  const handleRejected = (fileRejections: any[]) => {
    let message = "File upload failed. Please check your file and try again."

    if (fileRejections.length > 0) {
      const rejection = fileRejections[0]
      if (rejection.errors) {
        const error = rejection.errors[0]
        if (error.code === "file-too-large") {
          message = "File is too large. Please use a file smaller than 10MB."
        } else if (error.code === "file-invalid-type") {
          message =
            "Unsupported file type. Please upload a KML file (.kml) or zipped shapefile (.zip)."
        } else if (error.code === "too-many-files") {
          message = "Please upload only one file at a time."
        }
      }
    }

    // Display error using the component's error handling system
    const fullMessage = `${message}\n\nSupported formats:\n• KML files (.kml)\n• Zipped shapefiles (.zip)`
    onError?.(fullMessage)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (acceptedFiles.length > 0) {
        handleDrop(acceptedFiles)
      } else if (rejectedFiles.length > 0) {
        handleRejected(rejectedFiles)
      }
    },
    accept: [".kml", ".zip"] as any,
    disabled: isProcessing,
    maxFiles: 1,
  })

  const dropzoneClass = cx(
    "border-2 border-dashed p-4 sm:p-6 rounded-lg transition-colors cursor-pointer",
    {
      "border-gray-300 hover:border-gray-400": !isDragActive && !isProcessing,
      "border-blue-400 bg-blue-50": isDragActive,
      "border-gray-200 bg-gray-50 cursor-not-allowed": isProcessing,
    }
  )

  return (
    <div className="space-y-4">
      {/* Upload Instructions */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          Upload Spatial Files
        </h4>
        <p className="text-sm text-gray-600 mb-3">
          Upload KML files or zipped ESRI shapefiles to define sale boundaries.
          Maximum file size: 10MB.
        </p>

        {/* Help Text */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
          <div className="ml-3">
            <h5 className="text-xs font-medium text-blue-800 mb-1">
              Supported File Formats:
            </h5>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>
                • <strong>KML files (.kml)</strong> - From Google Earth, ArcGIS,
                or other GIS software
              </li>
              <li>
                • <strong>Shapefiles (.zip)</strong> - Must include .shp, .shx,
                .dbf, and .prj files
              </li>
            </ul>
            <p className="text-xs text-blue-600 mt-2 font-medium">
              💡 Tip: Only polygon boundaries are supported. Points and lines
              will be filtered out.
            </p>
          </div>
        </div>
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps({
          className: dropzoneClass,
        })}
      >
        <input {...getInputProps()} />
        <div className="text-center">
          {isProcessing ? (
            <div className="flex flex-col items-center space-y-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="text-sm text-gray-600">{processingStatus}</p>
            </div>
          ) : (
            <>
              <div className="flex justify-center mb-2 sm:mb-3">
                <svg
                  className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <p className="text-sm text-gray-700 mb-1 sm:mb-2 px-2 text-center">
                {isDragActive
                  ? "Drop your file here"
                  : "Drag and drop your spatial file here"}
              </p>
              <p className="text-xs text-gray-500 mb-2 sm:mb-3 px-2 text-center">
                Supports KML (.kml) and zipped ESRI shapefiles (.zip)
              </p>
              <button
                type="button"
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
              >
                Choose File
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <div className="text-sm text-red-700">
                {error.split("\n").map((line, index) => (
                  <div key={index} className={index > 0 ? "mt-2" : ""}>
                    {line.startsWith("Suggestions:") ? (
                      <p className="font-medium">{line}</p>
                    ) : line.match(/^\d+\./) ? (
                      <p className="ml-2">• {line.substring(3)}</p>
                    ) : (
                      <p>{line}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
